/**
 * One validation regime for every form.
 *
 * A field says nothing until the person has left it with something in it:
 * passing through an empty field is not a mistake, and «… را وارد کنید»
 * waits for the form's button. A field left with something wrong in it
 * says so at once, and from then on its sentence follows every change, so a
 * fix clears it at once. Emptied again, it goes quiet again. Pressing the
 * form's button checks everything: each bad field shows its sentence, a
 * toast says how many need fixing with a «نمایش» that brings the first one
 * back, and the first one is scrolled into view and focused straight away.
 * The toast is the second signal; the sentence under the field is the first.
 * Field errors the server sends back land the same way.
 *
 * ## API
 *
 * ```ts
 * const form = useFormErrors<Key>(validate, { values?, scope?, announce? })
 * ```
 *
 * - `validate: () => FormErrors<Key>`: a pure check of the current values,
 *   called on every render. Returns a sentence per bad field; a falsy value
 *   or a missing key means the field is fine. Sentences state the rule:
 *   «شماره موبایل را کامل وارد کنید»، «کد ملی باید ۱۰ رقم باشد».
 * - `options.values`: the form's current values (any object keyed like the
 *   errors). When given, a server error stays under its field only until
 *   that field's value changes, and a field whose value is empty stays
 *   quiet until the submit. Without it, server errors stay until the next
 *   `submit()` or `reset()`, and emptiness is read when the field is left.
 * - `options.scope`: a ref to the form's element. The first bad field is
 *   looked for inside it; without it, in the whole document. Give it when a
 *   page holds two forms, or a form sits in a modal over a page.
 * - `options.announce`: `false` drops the refusal toast (default `true`).
 *
 * Returns:
 *
 * - `errors`: the sentences to show now, by key.
 * - `errorOf(key)`: one of them, for `<Field error={...}>`.
 * - `invalidOf(key)`: whether that field shows an error, for `invalid`.
 * - `control(key)`: `{ invalid, onBlur }`, spread onto a kit control
 *   (`Input`, `Select`, `Textarea`, `DateField`, `TimeField`). Its onBlur
 *   reads whether the field was left empty: from the blur's own field, or
 *   from a picker's `{ blank }`.
 * - `touch(key, left?)`: marks a field as left. Pass a hand-written blur's
 *   event, or a control's `{ blank }`, so an empty field stays quiet; with
 *   neither, the field counts as filled. For controls without a blur
 *   (`Segmented`, `Check`, `Radio`), call it on change. For `UploadField`,
 *   call it in `onRemove` and once the stored file is in the form's values,
 *   never in `onAdd`: until the upload is done the file is not in `files`,
 *   and a required-file sentence would sit under the tile that is sending it.
 * - `isTouched(key)`, `submitted`.
 * - `valid`: whether `validate()` finds nothing right now.
 * - `count`: how many fields `validate()` refuses right now.
 * - `submit()`: checks everything, shows every sentence, toasts the count
 *   with «نمایش», scrolls to and focuses the first bad field (see below),
 *   and returns `true` when the form may be sent. It also drops the server
 *   errors of the previous attempt.
 * - `serverErrors(fields, { announce? })`: takes `ApiError.fields` (keys must
 *   be the form's keys; map them first if the API names them differently),
 *   shows them, announces them like a refused submit, and returns how many
 *   it took. Pass `null` or `{}` to clear them.
 * - `showFirst()`: scrolls to and focuses the first bad field now.
 * - `reset()`: forgets what was left, submitted and refused by the server.
 *
 * ## Use
 *
 * ```tsx
 * const form = useFormErrors(() => ({
 *   mobile: !isMobileShaped(v.mobile) && 'شماره موبایل را کامل وارد کنید.',
 *   name: v.name.trim().length < 2 && 'نام را وارد کنید.',
 * }), { values: v })
 *
 * <Field label="شماره موبایل" error={form.errorOf('mobile')}>
 *   <Input numeric dir="ltr" placeholder={FIELD_EXAMPLE.mobile}
 *     value={v.mobile} onChange={...} {...form.control('mobile')} />
 * </Field>
 *
 * const send = async () => {
 *   if (!form.submit()) return
 *   try { await api.post(...) }
 *   catch (e) { if (e instanceof ApiError && form.serverErrors(e.fields)) return; throw e }
 * }
 * ```
 *
 * For the first bad field to be found, its control must carry
 * `aria-invalid="true"`, which every kit control does when `invalid` is set.
 * A control without one is found through a `data-field="<key>"` on it or on
 * its wrapper, or through the error line its `Field` draws. Of all of these
 * the one highest on the page wins. The focus then goes to the field's
 * refused control (the one with `aria-invalid`), or else to its first
 * control, a kit `Check` or `Radio` included: a radio group gets it on its
 * chosen radio, or on its first one.
 */

import { useEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'
import { toPersianDigits } from '@metal/shared'
import { useToast } from '../surface/Toast'
import { prefersReducedMotion } from '../motion'

/** A sentence per field; a falsy value means the field is fine. */
export type FormErrors<K extends string = string> = Partial<Record<K, string | null | undefined | false>>

export interface UseFormErrorsOptions {
  /** The form's current values, keyed like the errors. */
  values?: object
  /** The element the form lives in. */
  scope?: RefObject<HTMLElement | null>
  /** Show the refusal toast on a refused submit or a server refusal. Default `true`. */
  announce?: boolean
}

/**
 * What a blur says about the field being left: a focus event, whose target
 * is the field, or a picker's own `{ blank }`. Nothing means not known.
 */
export type LeftField = { target?: EventTarget | null } | { blank: boolean } | undefined

/** Whether a blur left its field empty, when the blur can tell. */
function leftBlank(left: LeftField): boolean | undefined {
  if (!left) return undefined
  if ('blank' in left) return left.blank
  const el = left.target
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
    return el.value.trim() === ''
  }
  return undefined
}

/** A value with nothing in it: no text, no choice, no file, not ticked. */
function isBlank(value: unknown): boolean {
  if (value === undefined || value === null || value === false) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  return false
}

export interface FormErrorsApi<K extends string = string> {
  errors: Partial<Record<K, string>>
  errorOf: (key: K) => string | undefined
  invalidOf: (key: K) => boolean
  control: (key: K) => { invalid: boolean; onBlur: (left?: LeftField) => void }
  touch: (key: K, left?: LeftField) => void
  isTouched: (key: K) => boolean
  submitted: boolean
  valid: boolean
  count: number
  submit: () => boolean
  serverErrors: (
    fields: Partial<Record<string, string>> | null | undefined,
    opts?: { announce?: boolean },
  ) => number
  showFirst: () => void
  reset: () => void
}

/** «یک مورد در فرم نیاز به اصلاح دارد.» / «۳ مورد در فرم نیاز به اصلاح دارد.» */
export function refusalFa(count: number): string {
  return count === 1
    ? 'یک مورد در فرم نیاز به اصلاح دارد.'
    : `${toPersianDigits(count)} مورد در فرم نیاز به اصلاح دارد.`
}

const FOCUSABLE =
  'input:not([type="hidden"]):not([type="file"]):not(.visually-hidden):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * A tick or a radio. The kit's `Check` and `Radio` draw their real input
 * visually hidden, which `FOCUSABLE` leaves out, but the input still takes
 * the focus and is the control a person fixes.
 */
const CHOICE = 'input[type="checkbox"]:not([disabled]), input[type="radio"]:not([disabled])'

/**
 * Focusable, but never where a person fixes a field: the «i» beside a
 * label, and a file tile's remove button (an Enter there deletes a file).
 */
const NOT_THE_CONTROL = '.label-row *, .infotip-btn, .upf-remove'

/**
 * The first bad field, brought into view with the focus on it.
 *
 * Every mark counts: a control with `[aria-invalid="true"]`, a
 * `[data-field]` whose key is in `keys`, a `Field` that draws an error line.
 * The one highest on the page wins, so a choice refused above a text box is
 * shown before the text box. The focus goes to the field's own control; a
 * hidden one (a file input) hands it to the visible control beside it.
 */
export function showFirstInvalid(scope?: HTMLElement | null, keys?: readonly string[]): void {
  // A frame later, so a call made right after a state change sees the marks
  // that change draws.
  window.requestAnimationFrame(() => revealFirstInvalid(scope, keys))
}

/** The same, at once, for a caller that knows the marks are already drawn. */
function revealFirstInvalid(scope?: HTMLElement | null, keys?: readonly string[]): void {
  const target = firstInvalid(scope ?? document, keys)
  if (!target) return
  // A visually hidden input has no place of its own on the page; its label does.
  const shown = target.matches('.visually-hidden')
    ? (target.closest<HTMLElement>('label, [data-field], .field') ?? target)
    : target
  shown.scrollIntoView({ block: 'center', behavior: 'smooth' })
  controlOf(target)?.focus({ preventScroll: true })
  nudge(shown.closest<HTMLElement>('[data-field], .field') ?? shown)
}

/**
 * A short shake of the refused field once the scroll has brought it in, so
 * the eye lands on it. It goes both ways, so it reads the same in either
 * direction, and stays still for a reader who asked for no motion.
 */
function nudge(el: HTMLElement): void {
  if (prefersReducedMotion()) return
  el.animate?.(
    [
      { transform: 'none' },
      { transform: 'translateX(-6px)' },
      { transform: 'translateX(5px)' },
      { transform: 'translateX(-3px)' },
      { transform: 'translateX(2px)' },
      { transform: 'none' },
    ],
    { duration: 420, delay: 260, easing: 'ease-out' },
  )
}

function firstInvalid(root: ParentNode, keys?: readonly string[]): HTMLElement | null {
  const marked: HTMLElement[] = [...root.querySelectorAll<HTMLElement>('[aria-invalid="true"]')]
  if (keys?.length) {
    const wanted = new Set(keys)
    for (const el of root.querySelectorAll<HTMLElement>('[data-field]')) {
      if (wanted.has(el.dataset.field ?? '')) marked.push(el)
    }
  }
  for (const line of root.querySelectorAll<HTMLElement>('.field .err')) {
    marked.push(line.closest<HTMLElement>('.field') ?? line)
  }
  // Document order; a field comes before the control inside it, which is
  // the same field.
  let first: HTMLElement | null = null
  for (const el of marked) {
    if (!first || first.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING) first = el
  }
  return first
}

/**
 * Where the focus goes for a bad field: its own control, never the «i» or a
 * remove button.
 *
 * The refused control in the field wins: one marked `aria-invalid`, or the
 * first control inside a marked wrapper (a radio group). Failing that, the
 * first control in the field, a hidden tick or radio included. A radio group
 * takes the focus on its chosen radio, or on its first one when none is
 * chosen, the way Tab enters it. A field with no control left to focus
 * (every choice disabled) takes the focus itself, out of the Tab order, and
 * gives that up again when the focus leaves it.
 */
function controlOf(target: HTMLElement): HTMLElement | null {
  if (isControl(target)) return target.matches(CHOICE) ? (chosenRadio(target) ?? target) : target
  // A hidden file input stands for its upload's add tile; anything else for
  // the field (or `data-field` wrapper) around it.
  const box =
    target.closest<HTMLElement>('.upf') ?? target.closest<HTMLElement>('[data-field], .field') ?? target
  // A tick drawn above a refused text box does not take its focus.
  for (const bad of box.querySelectorAll<HTMLElement>('[aria-invalid="true"]')) {
    const own = isControl(bad) ? bad : firstControlIn(bad)
    if (own) return own.matches(CHOICE) ? (chosenRadio(own, box) ?? own) : own
  }
  const own = firstControlIn(box)
  if (own) return own.matches(CHOICE) ? (chosenRadio(own, box) ?? own) : own
  if (!box.hasAttribute('tabindex')) {
    // React does not own this attribute, so it goes when the focus does.
    box.setAttribute('tabindex', '-1')
    box.addEventListener('blur', () => box.removeAttribute('tabindex'), { once: true })
  }
  return box
}

/** A control a person fixes: focusable, or a hidden tick or radio; never the «i» or a remove button. */
function isControl(el: HTMLElement): boolean {
  return (el.matches(FOCUSABLE) || el.matches(CHOICE)) && !el.matches(NOT_THE_CONTROL)
}

/** The first control inside `box`, in document order. */
function firstControlIn(box: HTMLElement): HTMLElement | null {
  return (
    [...box.querySelectorAll<HTMLElement>(`${FOCUSABLE}, ${CHOICE}`)].find((el) => !el.matches(NOT_THE_CONTROL)) ??
    null
  )
}

/** The chosen radio of `radio`'s group, or the group's first enabled one. */
function chosenRadio(radio: HTMLElement, within?: HTMLElement): HTMLElement | null {
  if (!(radio instanceof HTMLInputElement) || radio.type !== 'radio') return null
  const root = within ?? radio.closest<HTMLElement>('[role="radiogroup"], [data-field], .field, form') ?? radio.parentElement
  if (!root) return null
  const group = [...root.querySelectorAll<HTMLInputElement>('input[type="radio"]:not([disabled])')].filter(
    (el) => el.name === radio.name,
  )
  return group.find((el) => el.checked) ?? group[0] ?? null
}

export function useFormErrors<K extends string = string>(
  validate: () => FormErrors<K>,
  options: UseFormErrorsOptions = {},
): FormErrorsApi<K> {
  const { values, scope, announce = true } = options
  const toast = useToast()
  /** Each field left so far, and whether it was empty when it was left. */
  const [touched, setTouched] = useState<Partial<Record<K, 'filled' | 'empty'>>>({})
  const [submitted, setSubmitted] = useState(false)
  const [server, setServer] = useState<ServerErrors>(NO_SERVER_ERRORS)
  /** Bumped to ask for the first bad field once the errors are on screen. */
  const [reveal, setReveal] = useState(0)
  const revealKeys = useRef<string[]>([])

  const live = clean<K>(validate())
  const current = values as Record<string, unknown> | undefined

  /** Empty now: from the form's values when it gave them, else as it was left. */
  const blankNow = (key: K) =>
    current && key in current ? isBlank(current[key]) : touched[key] === 'empty'

  const errors: Partial<Record<K, string>> = {}
  for (const key of Object.keys(live) as K[]) {
    if (submitted || (touched[key] && !blankNow(key))) errors[key] = live[key]
  }
  for (const key of Object.keys(server.fields) as K[]) {
    if (errors[key]) continue
    if (current && key in server.at && !sameValue(current[key], server.at[key])) continue
    errors[key] = server.fields[key]
  }

  useEffect(() => {
    if (reveal === 0) return
    // After the commit that drew the marks, so no frame needs waiting for.
    revealFirstInvalid(scope?.current, revealKeys.current)
    // Only a new request moves the focus; a scope change alone does not.
  }, [reveal])

  const refuse = (keys: string[], announceThis: boolean) => {
    revealKeys.current = keys
    if (announceThis) {
      toast.push({
        icon: 'alert',
        message: refusalFa(keys.length),
        action: {
          label: 'نمایش',
          onClick: () => revealFirstInvalid(scope?.current, revealKeys.current),
        },
      })
    }
    setReveal((n) => n + 1)
  }

  const touch = (key: K, blank = false) => {
    const state = blank ? 'empty' : 'filled'
    if (touched[key] === state) return
    setTouched((prev) => (prev[key] === state ? prev : { ...prev, [key]: state }))
  }

  return {
    errors,
    errorOf: (key) => errors[key],
    invalidOf: (key) => Boolean(errors[key]),
    control: (key) => ({
      invalid: Boolean(errors[key]),
      onBlur: (left?: LeftField) => touch(key, leftBlank(left) ?? false),
    }),
    touch: (key, left) => touch(key, leftBlank(left) ?? false),
    isTouched: (key) => Boolean(touched[key]),
    submitted,
    valid: Object.keys(live).length === 0,
    count: Object.keys(live).length,
    submit: () => {
      const found = clean<K>(validate())
      const keys = Object.keys(found)
      setServer(NO_SERVER_ERRORS)
      setSubmitted(true)
      if (keys.length === 0) return true
      refuse(keys, announce)
      return false
    },
    serverErrors: (fields, opts) => {
      const taken: Record<string, string> = {}
      for (const [key, sentence] of Object.entries(fields ?? {})) {
        if (sentence) taken[key] = sentence
      }
      const keys = Object.keys(taken)
      const at: Record<string, unknown> = {}
      if (current) for (const key of keys) at[key] = current[key]
      setServer(keys.length ? { fields: taken, at } : NO_SERVER_ERRORS)
      if (keys.length) refuse(keys, opts?.announce ?? announce)
      return keys.length
    },
    showFirst: () => showFirstInvalid(scope?.current, Object.keys(errors)),
    reset: () => {
      setTouched({})
      setSubmitted(false)
      setServer(NO_SERVER_ERRORS)
    },
  }
}

interface ServerErrors {
  fields: Record<string, string>
  /** Each field's value when the server refused it. */
  at: Record<string, unknown>
}

const NO_SERVER_ERRORS: ServerErrors = { fields: {}, at: {} }

function clean<K extends string>(found: FormErrors<K>): Partial<Record<K, string>> {
  const out: Partial<Record<K, string>> = {}
  for (const key of Object.keys(found) as K[]) {
    const sentence = found[key]
    if (typeof sentence === 'string' && sentence) out[key] = sentence
  }
  return out
}

function sameValue(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false
  try {
    return JSON.stringify(a) === JSON.stringify(b)
  } catch {
    return false
  }
}
